/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { WithChildren } from "../../helpers";
import { getKey, setKey } from "../../../Global/history";

export interface PageLink {
  title: string;
  path: string;
  state?: any;
  isActive: boolean;
  isSeparator?: boolean;
}

export interface PageDataContextModel {
  pageTitle?: string;
  setPageTitle: (_title: string) => void;
  pageDescription?: string;
  setPageDescription: (_description: string) => void;
  pageBreadcrumbs?: Array<PageLink>;
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
  previousPageTitle?: string;
  previousPagePath?: string;
  previousPageState?: any;
  // Shared layout H1 flag. Defaults true via <PageTitle>; set false to opt out.
  pageShowHeading?: boolean;
  setPageShowHeading: (_show: boolean) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => { },
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => { },
  setPageDescription: (_description: string) => { },
  setPageShowHeading: (_show: boolean) => { },
});

const PageDataProvider: FC<WithChildren> = ({ children }) => {
  const [pageTitle, setPageTitle] = useState<string>("");
  const [pageDescription, setPageDescription] = useState<string>("");
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
  const [pageShowHeading, setPageShowHeading] = useState<boolean>(false);
  const [previousPageTitle, setPreviousPageTitle] = useState<string | undefined>();
  const [previousPagePath, setPreviousPagePath] = useState<string | undefined>();
  const [previousPageState, setPreviousPageState] = useState<any>();

  const location = useLocation();
  const lastPathRef = useRef<string>(location.pathname);
  const lastTitleRef = useRef<string>("");
  const lastStateRef = useRef<any>(location.state);

  // On first load, restore the previously stored "previous page" title
  // so that a full page refresh keeps the same back label.
  useEffect(() => {
    const storedTitle = getKey("previousPageTitle");
    const storedPath = getKey("previousPagePath");
    const storedState = getKey("previousPageState");
    if (typeof storedTitle === "string" && storedTitle) {
      setPreviousPageTitle(storedTitle);
    }
    if (typeof storedPath === "string" && storedPath) {
      setPreviousPagePath(storedPath);
    }
    if (storedState) {
      setPreviousPageState(storedState);
    }
  }, []);

  useEffect(() => {
    // When the route path changes, freeze the title and state of the
    // page we are leaving as the "previous" page title and state.
    if (location.pathname !== lastPathRef.current) {
      const titleToSave = lastTitleRef.current || getKey("pageTitle") || "";
      if (titleToSave) {
        setPreviousPageTitle(titleToSave);
        setPreviousPagePath(lastPathRef.current);
        setPreviousPageState(lastStateRef.current);
        setKey("previousPageTitle", titleToSave);
        setKey("previousPagePath", lastPathRef.current);
        setKey("previousPageState", lastStateRef.current);
      }
      lastPathRef.current = location.pathname;
    }

    // Always keep track of the latest non-empty title and state so it can
    // be used on the next navigation as the previous page title and state.
    if (pageTitle) {
      lastTitleRef.current = pageTitle;
      setKey("pageTitle", pageTitle);
    }
    lastStateRef.current = location.state;
  }, [location.pathname, location.state, pageTitle]);

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    previousPageTitle,
    previousPagePath,
    previousPageState,
    pageShowHeading,
    setPageShowHeading,
  };
  return (
    <PageDataContext.Provider value={value}>
      {children}
    </PageDataContext.Provider>
  );
};

function usePageData() {
  return useContext(PageDataContext);
}

type Props = {
  description?: string;
  breadcrumbs?: Array<PageLink>;
  // Default true (shared H1). Set false only for custom/dynamic inline titles.
  showHeading?: boolean;
};

const PageTitle: FC<Props & WithChildren> = ({
  children,
  description,
  breadcrumbs,
  // Industry default: breadcrumb + shared H1. Pass showHeading={false} only when
  // the page renders a custom/dynamic title via AdminPageHeader.
  showHeading = true,
}) => {
  const { setPageTitle, setPageDescription, setPageBreadcrumbs, setPageShowHeading } =
    usePageData();
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString());
    }
    return () => {
      setPageTitle("");
    };
  }, [children]);

  useEffect(() => {
    setPageShowHeading(!!showHeading);
    return () => {
      setPageShowHeading(false);
    };
  }, [showHeading]);

  useEffect(() => {
    if (description) {
      setPageDescription(description);
    }
    return () => {
      setPageDescription("");
    };
  }, [description]);

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs);
    }
    return () => {
      setPageBreadcrumbs([]);
    };
  }, [breadcrumbs]);

  return <></>;
};

const PageDescription: FC<WithChildren> = ({ children }) => {
  const { setPageDescription } = usePageData();
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString());
    }
    return () => {
      setPageDescription("");
    };
  }, [children]);
  return <></>;
};

export { PageDescription, PageTitle, PageDataProvider, usePageData };
